import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Radio, PlayCircle, FileText, BellRing, CalendarDays, Mail } from "lucide-react";
import { API, errorText } from "@/lib/api";
import { Logo } from "@/components/landing/Logo";
import { Checkbox } from "@/components/ui/checkbox";
import { trackConversion } from "@/lib/track";
import { LEGAL_LINE, CONTACT_EMAIL } from "@/lib/site";
import { PhoneVerify } from "@/components/PhoneVerify";

const PERKS = [
  [Radio, "Live AI classes", "Practical sessions on LLMs, prompting and AI agents, streamed live on LinkedIn and YouTube."],
  [PlayCircle, "Every recording in one place", "Missed a live? Rewatch it in your portal instead of hunting through posts."],
  [FileText, "Class notes and slides", "The material from each class, ready when the live ends."],
  [BellRing, "A reminder before each class", "An email about an hour before we go live, with the link."],
];
const when = (iso) => new Date(iso).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" }) + " IST";
const inputCls = "h-12 w-full rounded-lg border border-slate-300 px-3 text-[15px] outline-none focus:border-[#0E9384] focus:ring-2 focus:ring-teal-100";

export default function FreePage() {
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", consent_marketing: false });
  const [ph, setPh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  useEffect(() => {
    document.title = "Free live AI classes | ArtOfResume";
    axios.get(`${API}/free/upcoming`).then((r) => setUpcoming(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!ph) return toast.error("Please verify your mobile number first.");
    setBusy(true);
    const source = params.get("utm_source") || params.get("src") || (document.referrer ? new URL(document.referrer).hostname : "direct");
    try {
      await axios.post(`${API}/free/join`, { ...f, phone: ph.phone, phone_token: ph.token, source: source.slice(0, 120) });
      trackConversion("free_signup");
      localStorage.setItem("aor_contact", JSON.stringify({ name: f.name, email: f.email }));
      setDone(true);
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="free-page" className="min-h-screen bg-[#F7FAFC]">
      <section className="grain relative overflow-clip bg-[#0B1B33] px-5 pb-16 pt-6 lg:px-10">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full border-[40px] border-[#0E9384]/40" />
        <div className="relative mx-auto max-w-[1200px]">
          <Logo light sub="FREE LIVE CLASSES" testId="free-logo" />
          <div className="mt-12 grid items-start gap-10 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <p className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[12px] font-semibold uppercase tracking-[0.14em] text-teal-300"><Radio className="h-3.5 w-3.5" /> Free · live every week</p>
              <h1 className="font-display mt-4 text-[40px] font-extrabold leading-[1.03] tracking-tight text-white md:text-[56px]">Learn practical AI live, for free.</h1>
              <p className="mt-4 max-w-xl text-[17px] text-slate-300">Sign up once and get every live link, recording and set of class notes in your own portal. No payment, no card, no spam.</p>
              <ul className="mt-8 grid gap-4 sm:grid-cols-2">{PERKS.map(([I, t, d]) => (
                <li key={t} className="flex gap-3"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#0E9384]/20 text-teal-300"><I className="h-5 w-5" /></span>
                  <span><b className="block text-white">{t}</b><span className="text-[14px] text-slate-400">{d}</span></span></li>))}</ul>
            </div>

            <div data-testid="free-card" className="rounded-2xl bg-white p-6 shadow-2xl md:p-8">
              {done ? (
                <div data-testid="free-done" className="py-6 text-center">
                  <CheckCircle2 className="mx-auto h-14 w-14 text-[#0E9384]" />
                  <h2 className="font-display mt-3 text-[26px] font-bold text-[#0B1B33]">You're in, {f.name.split(" ")[0]}!</h2>
                  <p className="mt-2 text-[15px] text-slate-700">We've emailed <b>{f.email}</b> a one-click link to your portal. It works once and expires in 20 minutes.</p>
                  <p className="mt-4 flex items-center justify-center gap-1.5 text-[13.5px] text-slate-500"><Mail className="h-4 w-4" /> Not there? Check Promotions or Spam.</p>
                  <a href="/login" className="mt-5 inline-block text-[14px] font-semibold text-[#0E9384] underline">Request a new link</a>
                </div>
              ) : (
                <form data-testid="free-form" onSubmit={submit} className="space-y-3">
                  <h2 className="font-display text-[24px] font-bold text-[#0B1B33]">Get free access</h2>
                  <p className="text-[14px] text-slate-600">Takes under a minute. We verify your mobile by OTP, then email you a sign-in link.</p>
                  <input data-testid="free-name" required maxLength={80} placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
                  <input data-testid="free-email" required type="email" placeholder="Email address" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
                  <PhoneVerify value={ph} onChange={setPh} testId="free-phone" />
                  <label className="flex gap-2.5 pt-1 text-[13px] text-slate-700">
                    <Checkbox data-testid="free-consent" checked={f.consent_marketing} onCheckedChange={(v) => setF({ ...f, consent_marketing: !!v })} className="mt-0.5 data-[state=checked]:border-[#0E9384] data-[state=checked]:bg-[#0E9384]" />
                    Also email me when new courses or cohorts open. (Optional)
                  </label>
                  <button data-testid="free-submit" disabled={busy} className="btn-teal flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-[17px] font-semibold disabled:opacity-60">
                    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Join the free classes"}
                  </button>
                  <p className="text-center text-[12.5px] text-slate-500">Already joined? <a href="/login" className="font-semibold text-[#0E9384]">Sign in</a> · <a href="/privacy" className="underline">Privacy</a></p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-5 py-12 lg:px-10">
        <h2 className="font-display text-[26px] font-bold text-[#0B1B33]">Upcoming free live classes</h2>
        {upcoming.length ? (
          <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">{upcoming.map((s) => (
            <div key={s.id} data-testid="free-upcoming" className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="flex items-center gap-1.5 text-[13px] font-semibold text-[#0E9384]"><CalendarDays className="h-4 w-4" />{when(s.starts_at)}</p>
              <p className="font-display mt-2 text-[18px] font-bold text-[#0B1B33]">{s.title}</p>
              <p className="mt-1 text-[13.5px] text-slate-600">{s.duration_min} min · join link in your portal</p>
            </div>))}</div>
        ) : <p className="mt-3 text-[15px] text-slate-600">The next class date is announced soon. Join now and you'll get the link and a reminder as soon as it's scheduled.</p>}
        <p className="mt-10 max-w-3xl text-[13.5px] text-slate-500">Free classes are for learning. They don't come with a certificate or job placement. If you want structured, reviewed project work, the paid cohorts are on <a href="/go#pathway" className="font-semibold text-[#0E9384]">the learning pathway</a>.</p>
        <p data-testid="free-legal" className="mt-8 border-t border-slate-200 pt-4 text-[12.5px] text-slate-500">© {new Date().getFullYear()} Bharat Reborn Private Limited. {LEGAL_LINE} Contact: <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a></p>
      </section>
    </div>
  );
}
